import { useEffect, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";
import { PageHeader, Loading, ErrorNote, Empty, StatusPill, formatDate } from "./Page";

export type FieldType =
  | "text"
  | "email"
  | "tel"
  | "url"
  | "textarea"
  | "select"
  | "number"
  | "date"
  | "tags";

export interface Field {
  name: string;
  label: string;
  type: FieldType;
  required?: boolean;
  options?: readonly string[];
  placeholder?: string;
  /** Hint shown under the input — used for things the label can't carry. */
  help?: string;
}

export interface Column {
  name: string;
  label: string;
  /** Renders as a status pill rather than plain text. */
  pill?: boolean;
  date?: boolean;
  /** Renders as currency, right-aligned. */
  money?: boolean;
  /**
   * Display text for stored values, e.g. "property-management" → "Property
   * management". Values with no entry render as-is.
   */
  labels?: Readonly<Record<string, string>>;
  /** Shown when the row has no value — for fields newer than some records. */
  fallback?: string;
}

/**
 * A second dropdown filter, alongside the status one.
 *
 * Contracts need it: service line and status are independent questions, and
 * folding either into the section's tab bar would mix "what kind of work is
 * this" with "where is it up to", which are not the same axis.
 */
export interface SecondaryFilter {
  field: string;
  /** The unfiltered option, e.g. "All service lines". */
  allLabel: string;
  options: readonly { value: string; label: string }[];
  /** Assumed for rows with no value, so older records still match a filter. */
  fallback?: string;
}

/**
 * Sorts by one field, keeping blanks at the end whichever way it runs.
 *
 * Blanks last in both directions on purpose: a descending sort that leads with
 * twenty empty cells is technically correct and useless. Numbers compare as
 * numbers, everything else compares as a localised string, so "Billed" orders by
 * value rather than lexically ("9" before "10", not after).
 */
function compareBy(column: string, desc: boolean) {
  return (a: Record<string, unknown>, b: Record<string, unknown>): number => {
    const x = a[column];
    const y = b[column];

    const xBlank = x === null || x === undefined || x === "";
    const yBlank = y === null || y === undefined || y === "";
    if (xBlank && yBlank) return 0;
    if (xBlank) return 1;
    if (yBlank) return -1;

    const order =
      typeof x === "number" && typeof y === "number"
        ? x - y
        : String(x).localeCompare(String(y), "en-CA", { numeric: true });

    return desc ? -order : order;
  };
}

/**
 * A cell's display text: the column's own label for the value, its fallback
 * when the row has none, then the raw value.
 */
function cellText(value: unknown, col: Column): string {
  const raw = value === undefined || value === null || value === "" ? col.fallback : value;
  if (raw === undefined || raw === null || raw === "") return "—";
  return col.labels?.[String(raw)] ?? String(raw);
}

/** Dollars, or an em dash when there is nothing to show. */
function formatMoney(value: unknown): string {
  if (typeof value !== "number" || Number.isNaN(value)) return "—";
  return value.toLocaleString("en-CA", {
    style: "currency",
    currency: "CAD",
    maximumFractionDigits: 0,
  });
}

interface Props {
  resource: string;
  title: string;
  fields: Field[];
  columns: Column[];
  statusField?: string;
  statuses?: readonly string[];
  emptyHint: string;
  /** Extra per-row buttons, e.g. converting a lead into a cleaner. */
  rowActions?: (row: Record<string, unknown>) => ReactNode;
  /**
   * Restricts the table to rows matching these values, and applies them to
   * anything created here.
   *
   * Filtered in the browser rather than by the API: these lists are hundreds of
   * rows at most, and one cached fetch shared between two views beats two
   * round trips that mostly return the same data.
   */
  where?: Record<string, unknown>;
  secondaryFilter?: SecondaryFilter;
  /** Column the table opens sorted by, before anyone clicks a header. */
  defaultSort?: { column: string; desc?: boolean };
}

type Record_ = Record<string, unknown>;

/**
 * List + create + edit + delete for one entity type.
 *
 * Leads, contracts, and cleaners differ only in their fields and columns,
 * so they share this and stay genuinely consistent instead of drifting into
 * three subtly different screens.
 */
export function CrudPage({
  resource,
  title,
  fields,
  columns,
  statusField,
  statuses,
  emptyHint,
  rowActions,
  where,
  secondaryFilter,
  defaultSort,
}: Props) {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Record_ | null>(null);
  const [creating, setCreating] = useState(false);
  const [filter, setFilter] = useState("ALL");
  const [filter2, setFilter2] = useState("ALL");
  /**
   * The row opened for a closer look.
   *
   * A table wide enough to be useful on a laptop cannot fit a phone, and the
   * fields worth reading -- a scraped ad in full, a revenue history -- were
   * never in a column anyway. So the row is a summary and this is the record.
   */
  const [detail, setDetail] = useState<Record_ | null>(null);
  const [search, setSearch] = useState("");
  /** Column to sort by, and direction. Newest-first by default, as before. */
  const [sort, setSort] = useState<{ column: string; desc: boolean } | null>(
    defaultSort ? { column: defaultSort.column, desc: defaultSort.desc ?? false } : null,
  );

  const { data, isLoading, error } = useQuery({
    queryKey: [resource],
    queryFn: () => api.list<Record_>(resource),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: [resource] });
    void qc.invalidateQueries({ queryKey: ["summary"] });
  };

  const save = useMutation({
    mutationFn: (values: Record_) =>
      editing
        ? api.update(resource, String(editing["id"]), values)
        : api.create(resource, { ...where, ...values }),
    onSuccess: () => {
      invalidate();
      setEditing(null);
      setCreating(false);
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.remove(resource, id),
    onSuccess: invalidate,
  });

  // Moving something through its pipeline is the most frequent edit by far, so
  // it happens in the row. Opening a form to change one dropdown is the kind of
  // friction that ends with nobody keeping the statuses current.
  const advance = useMutation({
    mutationFn: ({ id, value }: { id: string; value: string }) =>
      api.update(resource, id, { [statusField as string]: value }),
    onSuccess: invalidate,
  });

  if (isLoading) return <Loading />;
  if (error) return <ErrorNote error={error} />;

  const all = where
    ? (data ?? []).filter((r) =>
        Object.entries(where).every(([key, value]) => r[key] === value),
      )
    : (data ?? []);

  const byStatus =
    statusField && filter !== "ALL"
      ? all.filter((r) => r[statusField] === filter)
      : all;

  const bySecondary =
    secondaryFilter && filter2 !== "ALL"
      ? byStatus.filter(
          (r) => (r[secondaryFilter.field] ?? secondaryFilter.fallback) === filter2,
        )
      : byStatus;

  /**
   * Searches every field, not only the visible columns.
   *
   * The useful string is often one that never made it into a column -- a phone
   * number, a town buried in an address, a name inside a scraped ad -- and a
   * search that only looks at what is on screen fails exactly when it is needed.
   */
  const needle = search.trim().toLowerCase();
  const searched = needle
    ? bySecondary.filter((row) =>
        Object.values(row).some((value) => {
          if (value === null || value === undefined) return false;
          if (typeof value === "object") return JSON.stringify(value).toLowerCase().includes(needle);
          return String(value).toLowerCase().includes(needle);
        }),
      )
    : bySecondary;

  const items = sort ? [...searched].sort(compareBy(sort.column, sort.desc)) : searched;

  const formOpen = creating || editing !== null;

  return (
    <>
      <PageHeader
        title={title}
        count={items.length}
        actions={
          <>
            <input
              className="select"
              type="search"
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label={`Search ${title.toLowerCase()}`}
            />
            {secondaryFilter && (
              <select
                className="select"
                value={filter2}
                aria-label={secondaryFilter.allLabel}
                onChange={(e) => setFilter2(e.target.value)}
              >
                <option value="ALL">{secondaryFilter.allLabel}</option>
                {secondaryFilter.options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            )}
            {statusField && statuses && (
              <select
                className="select"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              >
                <option value="ALL">All statuses</option>
                {statuses.map((s) => (
                  <option key={s} value={s}>
                    {s.toLowerCase().replace(/_/g, " ")}
                  </option>
                ))}
              </select>
            )}
            <button
              className="btn btn--primary btn--sm"
              onClick={() => {
                setEditing(null);
                setCreating(true);
              }}
            >
              Add {title.replace(/s$/, "").toLowerCase()}
            </button>
          </>
        }
      />

      {formOpen && (
        <RecordForm
          fields={fields}
          initial={editing ?? {}}
          submitting={save.isPending}
          error={save.error}
          onCancel={() => {
            setEditing(null);
            setCreating(false);
          }}
          onSubmit={(values) => save.mutate(values)}
        />
      )}

      {items.length === 0 && !formOpen ? (
        <Empty>
          {needle || filter !== "ALL" || filter2 !== "ALL" ? (
            <>
              <h2>Nothing matches</h2>
              <p className="muted">
                No {title.toLowerCase()} match that search or filter. There
                {all.length === 1 ? " is" : " are"} {all.length} in total.
              </p>
            </>
          ) : (
            <>
              <h2>No {title.toLowerCase()} yet</h2>
              <p className="muted">{emptyHint}</p>
            </>
          )}
        </Empty>
      ) : (
        items.length > 0 && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  {columns.map((col, i) => {
                    const active = sort?.column === col.name;
                    return (
                      <th key={col.name} className={i > 1 ? "col--extra" : undefined}>
                        <button
                          className="table__sort"
                          aria-label={`Sort by ${col.label}`}
                          onClick={() =>
                            setSort((prev) =>
                              prev?.column === col.name
                                ? // Third click clears it, back to the natural
                                  // newest-first order.
                                  prev.desc
                                  ? null
                                  : { column: col.name, desc: true }
                                : { column: col.name, desc: false },
                            )
                          }
                        >
                          {col.label}
                          <span className="table__sort-arrow" aria-hidden="true">
                            {active ? (sort.desc ? "↓" : "↑") : ""}
                          </span>
                        </button>
                      </th>
                    );
                  })}
                  <th aria-label="Actions" className="col--extra" />
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr
                    key={String(row["id"])}
                    className="table__row"
                    tabIndex={0}
                    role="button"
                    aria-label={`Open ${String(row["name"] ?? row["clientName"] ?? "record")}`}
                    onClick={() => setDetail(row)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setDetail(row);
                      }
                    }}
                  >
                    {columns.map((col, i) => (
                      <td
                        key={col.name}
                        // Everything past the first two columns is hidden on a
                        // phone; the detail sheet carries it instead.
                        className={i > 1 ? "col--extra" : undefined}
                        // A select inside a row that opens on click would open
                        // the sheet every time the status changed.
                        onClick={col.name === statusField ? (e) => e.stopPropagation() : undefined}
                      >
                        {col.name === statusField && statuses ? (
                          <select
                            className="select select--inline"
                            value={String(row[col.name] ?? "")}
                            disabled={advance.isPending}
                            onChange={(e) =>
                              advance.mutate({
                                id: String(row["id"]),
                                value: e.target.value,
                              })
                            }
                          >
                            {statuses.map((s) => (
                              <option key={s} value={s}>
                                {s.toLowerCase().replace(/_/g, " ")}
                              </option>
                            ))}
                          </select>
                        ) : col.money ? (
                          formatMoney(row[col.name])
                        ) : col.pill && row[col.name] ? (
                          <StatusPill status={String(row[col.name])} />
                        ) : col.date ? (
                          formatDate(row[col.name] as string)
                        ) : Array.isArray(row[col.name]) ? (
                          (row[col.name] as string[]).join(", ") || "—"
                        ) : (
                          cellText(row[col.name], col)
                        )}
                      </td>
                    ))}
                    <td
                      className="table__actions col--extra"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {rowActions?.(row)}
                      <button
                        className="btn btn--sm btn--ghost"
                        onClick={() => {
                          setCreating(false);
                          setEditing(row);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn--sm btn--danger"
                        disabled={remove.isPending}
                        onClick={() => {
                          if (
                            window.confirm(
                              `Delete "${row["name"] ?? row["clientName"] ?? "this record"}"? This cannot be undone.`,
                            )
                          ) {
                            remove.mutate(String(row["id"]));
                          }
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}

      {detail && (
        <RowDetail
          row={detail}
          fields={fields}
          statuses={statusField ? statuses : undefined}
          busy={advance.isPending || remove.isPending}
          onStatus={
            statusField
              ? (value) => advance.mutate({ id: String(detail["id"]), value })
              : undefined
          }
          onEdit={() => {
            setCreating(false);
            setEditing(detail);
            setDetail(null);
          }}
          onDelete={() => {
            if (window.confirm(`Delete "${String(detail["name"] ?? "this record")}"?`)) {
              remove.mutate(String(detail["id"]));
              setDetail(null);
            }
          }}
          extraActions={rowActions?.(detail)}
          onClose={() => setDetail(null)}
        />
      )}

      {remove.error && <ErrorNote error={remove.error} />}
      {advance.error != null && <ErrorNote error={advance.error} />}
    </>
  );
}

/**
 * Everything about one record, on a screen of any size.
 *
 * A sheet rather than a wider table: the useful fields are long -- a scraped ad,
 * a set of notes -- and no column width makes those readable. On a phone it
 * covers the screen; on a laptop it sits over the list.
 */
function RowDetail({
  row,
  fields,
  statuses,
  busy,
  onStatus,
  onEdit,
  onDelete,
  extraActions,
  onClose,
}: {
  row: Record_;
  fields: Field[];
  statuses?: readonly string[] | undefined;
  busy: boolean;
  onStatus?: ((value: string) => void) | undefined;
  onEdit: () => void;
  onDelete: () => void;
  extraActions?: ReactNode;
  onClose: () => void;
}) {
  // Escape closes it, which is the one keyboard convention every overlay owes.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const title = String(row["name"] ?? row["clientName"] ?? "Record");

  return (
    <div className="sheet" role="dialog" aria-modal="true" aria-label={title}>
      <button className="sheet__scrim" aria-label="Close" onClick={onClose} />
      <div className="sheet__panel">
        <header className="sheet__head">
          <h2>{title}</h2>
          <button className="sheet__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        {statuses && onStatus && (
          <label className="field">
            <span className="field__label">Status</span>
            <select
              value={String(row["status"] ?? "")}
              disabled={busy}
              onChange={(e) => onStatus(e.target.value)}
            >
              {statuses.map((s) => (
                <option key={s} value={s}>
                  {s.toLowerCase().replace(/_/g, " ")}
                </option>
              ))}
            </select>
          </label>
        )}

        <dl className="sheet__fields">
          {fields.map((field) => {
            const value = row[field.name];
            if (value === undefined || value === null || value === "") return null;
            const text = Array.isArray(value) ? value.join(", ") : String(value);
            if (!text) return null;

            return (
              <div key={field.name}>
                <dt>{field.label}</dt>
                <dd>
                  {field.type === "url" ? (
                    <a href={text} target="_blank" rel="noreferrer noopener">
                      {text}
                    </a>
                  ) : field.type === "tel" ? (
                    // On a phone this is the whole point: tap it and it dials.
                    <a href={`tel:${text.replace(/[^\d+]/g, "")}`}>{text}</a>
                  ) : field.type === "email" ? (
                    <a href={`mailto:${text}`}>{text}</a>
                  ) : (
                    text
                  )}
                </dd>
              </div>
            );
          })}
          {typeof row["createdAt"] === "string" && (
            <div>
              <dt>Added</dt>
              <dd>{formatDate(row["createdAt"] as string)}</dd>
            </div>
          )}
        </dl>

        <div className="sheet__actions">
          {extraActions}
          <button className="btn btn--sm btn--ghost" onClick={onEdit}>
            Edit
          </button>
          <button className="btn btn--sm btn--danger" disabled={busy} onClick={onDelete}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function RecordForm({
  fields,
  initial,
  submitting,
  error,
  onSubmit,
  onCancel,
}: {
  fields: Field[];
  initial: Record_;
  submitting: boolean;
  error: unknown;
  onSubmit: (values: Record_) => void;
  onCancel: () => void;
}) {
  return (
    <form
      className="form-card"
      onSubmit={(e) => {
        e.preventDefault();
        const data = new FormData(e.currentTarget);
        const values: Record_ = {};

        for (const field of fields) {
          const raw = String(data.get(field.name) ?? "").trim();

          // Omit empty optionals entirely rather than sending "" — the zod
          // schemas treat an empty string as a validation failure, not as absent.
          if (!raw) continue;

          if (field.type === "number") values[field.name] = Number(raw);
          else if (field.type === "tags") {
            values[field.name] = raw
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean);
          } else values[field.name] = raw;
        }

        onSubmit(values);
      }}
    >
      <div className="form-grid">
        {fields.map((field) => {
          const value = initial[field.name];
          const defaultValue = Array.isArray(value)
            ? (value as string[]).join(", ")
            : value === undefined || value === null
              ? ""
              : String(value);

          return (
            <label
              key={field.name}
              className={`field ${field.type === "textarea" ? "field--wide" : ""}`}
            >
              <span className="field__label">
                {field.label}
                {field.required && <b aria-hidden="true"> *</b>}
              </span>

              {field.type === "select" ? (
                <select name={field.name} defaultValue={defaultValue} required={field.required}>
                  <option value="">Select…</option>
                  {field.options?.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt.toLowerCase().replace(/_/g, " ")}
                    </option>
                  ))}
                </select>
              ) : field.type === "textarea" ? (
                <textarea
                  name={field.name}
                  rows={3}
                  defaultValue={defaultValue}
                  required={field.required}
                  placeholder={field.placeholder}
                />
              ) : (
                <input
                  name={field.name}
                  type={
                    field.type === "tags"
                      ? "text"
                      : field.type === "number"
                        ? "number"
                        : field.type
                  }
                  step={field.type === "number" ? "0.01" : undefined}
                  defaultValue={defaultValue}
                  required={field.required}
                  placeholder={field.placeholder}
                />
              )}

              {field.help && <span className="field__help">{field.help}</span>}
            </label>
          );
        })}
      </div>

      {error != null && <ErrorNote error={error} />}

      <div className="form-card__actions">
        <button className="btn btn--primary" type="submit" disabled={submitting}>
          {submitting ? "Saving…" : "Save"}
        </button>
        <button className="btn btn--ghost" type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
